/**
 * `CapabilitySet` (`api/components/schemas/common/CapabilitySet.yaml`, `LLD-XCC-059`,
 * `LLD-SPA-026`). "The client renders from this and derives nothing" — no capability is ever
 * computed from a role or a token client-side; the served set is the only input.
 *
 * A React context rather than a store slice: it is fetched once per shell mount (keyed on the
 * organisation), read by many components, and never survives a sign-out or an organisation
 * switch — exactly a query's lifetime, not the global store's (`LLD-SPA-062` reserves the store
 * for cross-cutting state with no natural query owner).
 */
import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import type { components } from '@/api/generated/schema'

export type CapabilitySet = components['schemas']['CapabilitySet']
export type Capability = components['schemas']['Capability']

const CapabilityContext = createContext<CapabilitySet | undefined>(undefined)

export function CapabilityProvider({ orgId, children }: { orgId: string; children: ReactNode }) {
  const query = useQuery({
    queryKey: ['capabilities', orgId],
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/capabilities', {
        params: { path: { org_id: orgId } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as CapabilitySet
    },
    staleTime: 60_000,
  })

  return <CapabilityContext.Provider value={query.data}>{children}</CapabilityContext.Provider>
}

export function useCapabilitySet(): CapabilitySet | undefined {
  return useContext(CapabilityContext)
}

/** LLD-SPA-026: "the navigation tree is never consulted to decide what a caller may do" — every
 * caller of this reads the served set, never the route table. */
export function hasOrgCapability(caps: CapabilitySet | undefined, capability: Capability): boolean {
  return caps?.organisation.includes(capability) ?? false
}

export function hasWorkspaceCapability(caps: CapabilitySet | undefined, workspaceId: string, capability: Capability): boolean {
  return caps?.workspaces.find((w) => w.workspace_id === workspaceId)?.capabilities.includes(capability) ?? false
}
